import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, finalize, map, of, shareReplay, tap, throwError } from 'rxjs';

import { clearAllCache } from '../api/stale-while-revalidate';
import { AccessTokenState, AuthCookieStore } from './auth-cookie-store.service';

export type AuthStatus = 'unknown' | 'authenticated' | 'unauthenticated';

export type SessionResponse = {
  authenticated: boolean;
  user?: {
    user_id: string;
    email: string;
    name?: string | null;
    logo_url?: string | null;
  } | null;
};

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly cookies = inject(AuthCookieStore);

  private readonly _status = signal<AuthStatus>('unknown');
  readonly status = this._status.asReadonly();
  readonly isAuthenticated = computed(() => this.status() === 'authenticated');

  private refreshInFlight$?: Observable<void>;
  private sessionInFlight$?: Observable<boolean>;

  constructor() {
    this.bootstrapFromCookies();
  }

  probeSession(): Observable<boolean> {
    if (this.sessionInFlight$) return this.sessionInFlight$;

    this.sessionInFlight$ = this.http
      .get<SessionResponse>('/session', { withCredentials: true })
      .pipe(
        map((res) => !!res?.authenticated),
        tap((ok) => this._status.set(ok ? 'authenticated' : 'unauthenticated')),
        catchError(() => {
          this._status.set('unauthenticated');
          return of(false);
        }),
        finalize(() => {
          this.sessionInFlight$ = undefined;
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
      );

    return this.sessionInFlight$;
  }

  refreshIfNeeded(reason: 'guard' | '401'): Observable<void> {
    const accessState = this.cookies.getAccessTokenState();

    // Guard path: refresh only when we can *prove* the access token is expired.
    if (reason === 'guard' && accessState !== 'expired') {
      // - valid: no refresh needed
      // - missing/unknown: don't eagerly refresh; let API calls decide (or 401 interceptor).
      // (Also avoids overwriting freshly-set cookies right after /get_access.)
      return of(void 0);
    }

    // On 401 we treat refresh as necessary (server told us access is not accepted).
    return this.refresh();
  }

  refresh(): Observable<void> {
    if (this.refreshInFlight$) return this.refreshInFlight$;

    this.refreshInFlight$ = this.http.get('/token/refresh', { withCredentials: true }).pipe(
      map(() => void 0),
      tap(() => this._status.set('authenticated')),
      catchError((err) => {
        this._status.set('unauthenticated');
        return throwError(() => err);
      }),
      finalize(() => {
        this.refreshInFlight$ = undefined;
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    return this.refreshInFlight$;
  }

  markUnauthenticated() {
    this._status.set('unauthenticated');
  }

  markAuthenticated() {
    this._status.set('authenticated');
  }

  logout(): Observable<void> {
    return this.http.get('/logout', { withCredentials: true }).pipe(
      map(() => void 0),
      catchError(() => of(void 0)),
      tap(() => {
        clearAllCache();
        this._status.set('unauthenticated');
      }),
      tap(() => {
        void this.router.navigate(['/']);
      }),
    );
  }

  private bootstrapFromCookies() {
    const accessState: AccessTokenState = this.cookies.getAccessTokenState();

    if (accessState === 'valid' || accessState === 'unknown') {
      // unknown means we couldn't parse exp (opaque token) but cookie exists and is readable.
      this._status.set('authenticated');
      return;
    }

    if (accessState === 'expired') {
      this._status.set('unknown');
      return;
    }

    // missing
    // In HttpOnly-cookie setups, JS may not see tokens even when a session is valid.
    // Start in `unknown` and let `probeSession()` establish truth.
    this._status.set('unknown');
  }
}
